/**
 * Экран входа `/login` (Фаза 3, И3).
 *
 * Форма `{email, password}` → [`signIn`](../useAuth.tsx) (`POST /api/auth/login`).
 * 401 показываем единым «Неверный email или пароль» (сервер и так не раскрывает
 * существование email), 429 — дружелюбное «попробуйте позже». После успеха —
 * `onSuccess` (в App.tsx — переход в кабинет `/me`), причём НЕ сразу после
 * резолва signIn, а из эффекта при появлении `user` в состоянии провайдера —
 * иначе гонка с гейтом /me откатывала навигацию (см. ниже). Ссылки на `/register`
 * и `/forgot`.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, CAPTCHA_FAILED, CAPTCHA_REQUIRED, type CaptchaSolution } from '../api';
import { Captcha } from '../components/Captcha';
import { Link } from '../components/ui';
import { useAuth } from '../useAuth';

export interface LoginProps {
  /** Вызывается после успешного входа (обычно — редирект на главную). */
  onSuccess?: () => void;
}

export function Login({ onSuccess }: LoginProps): JSX.Element {
  const { t } = useTranslation();
  const { signIn, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Адаптивная капча (И8): изначально не видна; появляется, когда сервер вернёт
  // captcha_required (после K неудач с IP). captchaNonce перевыпускает новую при ошибке.
  const [captcha, setCaptcha] = useState<CaptchaSolution | null>(null);
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [captchaRequired, setCaptchaRequired] = useState(false);

  // Пост-логин навигация — только после того, как `user` реально появился в
  // состоянии AuthProvider (фикс гонки с гейтом /me). Раньше onSuccess звался
  // сразу после резолва signIn; в реальном браузере рендер со сменой роута на
  // /me успевал выполниться со старым user=null (синхронный флеш external-store
  // роутера обгонял апдейт контекста) — гейт /me мгновенно откатывал на /login:
  // пользователь оставался на сброшенной форме входа при живой сессии. Теперь
  // навигация выполняется из эффекта уже после коммита user; userRef покрывает
  // случай, когда user закоммичен ещё ДО резолва signIn (синхронный батч) —
  // тогда навигируем сразу, не ждём отдельный эффект-тик.
  const successPending = useRef(false);
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    if (successPending.current && user !== null) {
      successPending.current = false;
      onSuccess?.();
    }
  }, [user, onSuccess]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email.trim(), password, captcha ?? undefined);
      if (userRef.current !== null) onSuccess?.();
      else successPending.current = true;
    } catch (e) {
      // Адаптивная капча (И8): сервер требует (captcha_required) или не принял
      // (captcha_failed) решение. Показываем виджет и перевыпускаем новую (одноразовая).
      if (
        e instanceof ApiError &&
        (e.message === CAPTCHA_REQUIRED || e.message === CAPTCHA_FAILED)
      ) {
        setCaptchaRequired(true);
        setCaptchaNonce((n) => n + 1);
        setCaptcha(null);
        setError(
          e.message === CAPTCHA_REQUIRED ? t('errors.captchaRequired') : t('errors.captchaFailed'),
        );
      } else {
        setError(loginErrorMessage(t, e));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="card card--page" aria-labelledby="login-title">
      <h1 id="login-title" className="page-title">
        {t('login.title')}
      </h1>

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="form-row">
          <label className="field-label" htmlFor="login-email">
            {t('common.email')}
          </label>
          <input
            id="login-email"
            className="text-input"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </div>

        <div className="form-row">
          <label className="field-label" htmlFor="login-password">
            {t('common.password')}
          </label>
          <input
            id="login-password"
            className="text-input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />
        </div>

        {/* Адаптивная капча (И8): изначально скрыта; монтируется, когда сервер
            вернул captcha_required. key=nonce перевыпускает новую при ошибке. */}
        {captchaRequired ? <Captcha key={captchaNonce} onChange={setCaptcha} /> : null}

        {error ? (
          <p className="error" role="alert" data-testid="login-error">
            {error}
          </p>
        ) : null}

        <button type="submit" className="cta" disabled={submitting} data-testid="login-submit">
          {submitting ? t('login.submitting') : t('login.submit')}
        </button>
      </form>

      <p className="auth-switch">
        {t('login.noAccount')} <Link to="/register">{t('nav.register')}</Link>
      </p>
      <p className="auth-switch">
        <Link to="/forgot">{t('login.forgot')}</Link>
      </p>
      <p className="auth-switch">
        <Link to="/">{t('nav.toMain')}</Link>
      </p>
    </main>
  );
}

/** Дружелюбное сообщение по статусу ошибки входа (401/429/сеть). */
function loginErrorMessage(t: (key: string) => string, e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return t('errors.invalidCredentials');
    if (e.status === 429) return t('errors.loginRateLimited');
  }
  return t('errors.loginNetwork');
}
